import { Box } from "@mui/material";

import { ScanningIndicator, useScanIndicator } from "@qubit/autoparts";
import { isGuid } from "is-guid";
import { useCallback, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

import { CartTable } from "./CartTable";
import { CartsToolbar } from "./CartsToolbar";
import { scanBarcode, selectDate } from "./carts.slice";

type Props = { viewTitle?: ViewNameTranslation };

export function Carts(props: Props) {
  const { viewTitle } = props;
  const dispatch = useAppDispatch();
  const trackedPageName = useMixpanelPageName();

  const selectedDate = useAppSelector((state) => state.carts.selectedDate);
  const scannedBarcode = useAppSelector((state) => state.carts.scannedBarcode);
  const isBatchSelected = useAppSelector(
    (state) => !!state.carts.selectedBatch
  );

  const handleDateChange = useCallback(
    (date: Date | null): void => {
      mixpanelTrack({
        trackedPageName,
        type: "Input Change",
        label: "Select Date",
        eventProperties: {
          "Selected Date": date?.toISOString()
        }
      });
      dispatch(selectDate(date ?? new Date()));
    },
    [dispatch, trackedPageName]
  );

  const datePicker = useMemo(() => {
    return { selectedDate, onDateChange: handleDateChange };
  }, [selectedDate, handleDateChange]);

  const { setToolbar } = useNavbar({
    datePicker,
    viewTitle
  });

  useEffect(() => {
    if (isBatchSelected) {
      setToolbar(<CartsToolbar />);
    } else {
      setToolbar(undefined);
    }
  }, [isBatchSelected, setToolbar]);

  // reset the date each time the page is mounted
  useEffect(() => {
    dispatch(selectDate(new Date()));
  }, [dispatch]);

  const [scanState, setScanState] = useScanIndicator();

  useKeyDownHandler();
  useBarcodeScanner({
    findScanMatch: (toteId: string) => {
      if (isGuid(toteId)) {
        mixpanelTrack({
          trackedPageName,
          type: "Scan",
          label: "Scan Tote",
          eventProperties: {
            "Tote ID": toteId
          }
        });
        dispatch(scanBarcode(toteId));
        setScanState("success");
      }
    },
    deps: [trackedPageName]
  });

  return (
    <>
      <Box
        sx={{
          maxWidth: (theme) => theme.breakpoints.values.xl,
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          my: {
            xs: 1,
            md: 3
          },
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        <CartTable />
      </Box>
      <ScanningIndicator
        scanState={scanState}
        scannedBarcode={scannedBarcode}
        placeholderText="Scan Tote"
      />
    </>
  );
}
